// src/pages/GalleryUpload.js (v5 – size pre-checks + safe errors + multipart)

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { API_PATHS, ROUTES } from "../config";
import "../styles/GalleryUpload.css";

const MAX_ITEMS = 20;
const MAX_VIDEO_SECONDS = 10;
const VIDEO_TOLERANCE_SECONDS = 0.3; // Matches backend ~11s grace


const getVideoDurationMs = (file) =>
  new Promise((resolve) => {
    try {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const ms = duration > 0 ? Math.round(duration * 1000) : 0;
        URL.revokeObjectURL(video.src);
        resolve(ms);
      };

      video.onerror = () => {
        try {
          URL.revokeObjectURL(video.src);
        } catch (e) {
          // ignore: best-effort cleanup
        }
        resolve(0); // fall back — backend still handles processing; we just can't pre-validate
      };

      video.src = URL.createObjectURL(file);
    } catch (e) {
      resolve(0);
    }
  });

// Match backend defaults (photo 10MB, video 15MB)
const MAX_PHOTO_MB = Number(process.env.REACT_APP_MAX_PHOTO_MB || 10);
const MAX_VIDEO_MB = Number(process.env.REACT_APP_MAX_VIDEO_MB || 15);
const MAX_PHOTO_BYTES = MAX_PHOTO_MB * 1024 * 1024;
const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;

const prettyMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

const safeErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;

  // Common FastAPI: { detail: "..." }
  if (typeof detail === "string") return detail;

  // FastAPI validation: { detail: [ {msg, loc, ...}, ... ] }
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => d?.msg)
      .filter(Boolean)
      .slice(0, 3);
    return msgs.length ? msgs.join(" • ") : "Request was rejected by the server.";
  }

  // Some APIs: { detail: { ... } }
  if (detail && typeof detail === "object") {
    return "Request was rejected by the server.";
  }

  return err?.message || "Something went wrong.";
};

/**
 * Page for uploading multiple photos and short videos from desktop/laptop.
 * Supports up to 20 items, requires a caption/story for each,
 * enforces 10-second max video length, and auto-appends school hashtag/display name.
 */
const GalleryUpload = () => {
  const navigate = useNavigate();
  const [mediaItems, setMediaItems] = useState([]); // { file, preview, story, mediaType, durationMs?, tooLong? }
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [schoolHashtag, setSchoolHashtag] = useState("");
  const [displayName, setDisplayName] = useState("");
  const fileInputRef = useRef(null);

  // Fetch profile for hashtag and display name + warmup
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchProfileData = async () => {
      try {
        const { data } = await api.get(API_PATHS.PROFILE);
        const rawName = data.school_name || "";
        const fallbackFromName = rawName ? rawName.replace(/\s+/g, "") : "";

        setSchoolHashtag(data.school_hashtag || fallbackFromName || "");
        setDisplayName(data.display_name || "");
      } catch (err) {
        console.error("Failed to fetch profile data:", err);
        // Non-critical — continue without hashtags
      }
    };

    fetchProfileData();

    // 🔥 Trigger backend warmup (DB, Redis, Celery) on page load
    const triggerWarmup = async () => {
      try {
        await api.post(API_PATHS.WARMUP, {});
        console.log("🔥 Backend warmup triggered successfully");
      } catch (err) {
        console.warn("🔥 Warmup request failed (non-critical):", err?.message);
      }
    };

    triggerWarmup();
  }, []);

  const handlePickMedia = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const remainingSlots = MAX_ITEMS - mediaItems.length;
    if (remainingSlots <= 0) {
      setError(`You can upload up to ${MAX_ITEMS} items at a time.`);
      return;
    }

    const filesToConsider = selectedFiles.slice(0, remainingSlots);

    const accepted = [];
    const rejected = [];

    for (const file of filesToConsider) {
      const isVideo = file.type?.startsWith("video/");
      const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_PHOTO_BYTES;
      const maxMb = isVideo ? MAX_VIDEO_MB : MAX_PHOTO_MB;

      if (file.size > maxBytes) {
        rejected.push(
          `${file.name} (${prettyMB(file.size)}) exceeds max ${maxMb}MB for ${
            isVideo ? "videos" : "photos"
          }`
        );
        continue;
      }

      accepted.push({
        file,
        preview: URL.createObjectURL(file),
        story: "",
        mediaType: isVideo ? "VIDEO" : "PHOTO",
        durationMs: null,
        tooLong: false,
      });
    }

    if (accepted.length) {
      // Populate durationMs + tooLong for videos (client-side pre-check)
      const withDurations = await Promise.all(
        accepted.map(async (item) => {
          if (item.mediaType !== "VIDEO") return item;
          const ms = await getVideoDurationMs(item.file);
          const tooLong = ms > 0 && ms / 1000 > MAX_VIDEO_SECONDS;
          return { ...item, durationMs: ms, tooLong };
        })
      );

      setMediaItems((prev) => [...prev, ...withDurations]);
    }


    if (rejected.length) {
      setError(rejected.join(" • "));
    } else {
      setError("");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteItem = (preview) => {
    setMediaItems((prev) => {
      const updated = prev.filter((item) => item.preview !== preview);
      URL.revokeObjectURL(preview);
      return updated;
    });
  };

  const handleStoryChange = (preview, value) => {
    setMediaItems((prev) =>
      prev.map((item) =>
        item.preview === preview ? { ...item, story: value } : item
      )
    );
  };

  const handleVideoMetadata = (preview, event) => {
    const durationSec = event?.target?.duration;
    if (!durationSec || Number.isNaN(durationSec)) return;

    const durationMs = Math.round(durationSec * 1000);
    const isTooLong = durationSec > MAX_VIDEO_SECONDS + VIDEO_TOLERANCE_SECONDS;

    setMediaItems((prev) =>
      prev.map((item) =>
        item.preview === preview
          ? { ...item, durationMs, tooLong: isTooLong }
          : item
      )
    );
  };

  const hasTooLongVideo = mediaItems.some(
    (item) => item.mediaType === "VIDEO" && item.tooLong
  );

  const allStoriesFilled =
    mediaItems.length > 0 &&
    mediaItems.every((item) => item.story.trim().length > 0);

  const canUpload = mediaItems.length > 0 && allStoriesFilled && !hasTooLongVideo;

  const cleanedDisplayName = (displayName || "").replace(/[\s.]+/g, "");

  const handleUpload = async () => {
    if (!mediaItems.length) {
      setError("Please select at least one photo or video to upload.");
      return;
    }
    if (!allStoriesFilled) {
      setError("Please add a short story or caption for each photo or video.");
      return;
    }
    if (hasTooLongVideo) {
      setError(
        `One or more videos are longer than ${MAX_VIDEO_SECONDS} seconds. Please remove or trim them.`
      );
      return;
    }

    // Double-check size again (belt + braces)
    const oversize = mediaItems.find((item) => {
      const isVideo = item.mediaType === "VIDEO";
      const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_PHOTO_BYTES;
      return item.file?.size > maxBytes;
    });
    if (oversize) {
      const isVideo = oversize.mediaType === "VIDEO";
      const maxMb = isVideo ? MAX_VIDEO_MB : MAX_PHOTO_MB;
      setError(
        `${oversize.file?.name || "A file"} is too large (${prettyMB(
          oversize.file?.size || 0
        )}). Max ${maxMb}MB for ${isVideo ? "videos" : "photos"}.`
      );
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setError("You are not authorized. Please log in.");
      navigate(ROUTES.LOGIN);
      return;
    }

    const videoWithoutDuration = mediaItems.find(
      (item) => item.mediaType === "VIDEO" && item.durationMs == null
    );
    if (videoWithoutDuration) {
      setError("Still processing a video. Please wait a moment.");
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setError("");

    try {
      const totalItems = mediaItems.length;

      for (let i = 0; i < totalItems; i++) {
        const item = mediaItems[i];

        const formData = new FormData();
        formData.append("files", item.file);

        const storyWithHashtags = `${item.story.trim()} ${schoolHashtag || ""} ${
          cleanedDisplayName ? "#" + cleanedDisplayName : ""
        }`.trim();

        formData.append("stories", storyWithHashtags);
        formData.append("media_types", item.mediaType || "PHOTO");

        const durationMs = item.mediaType === "VIDEO" ? item.durationMs ?? 0 : 0;
        formData.append("video_durations_ms", durationMs);

        await api.post(API_PATHS.UPLOAD, formData, {
          // IMPORTANT: your axios instance defaults to JSON, so override for FormData
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) return;

            const itemProgress = progressEvent.loaded / progressEvent.total; // 0..1
            const overallProgress = ((i + itemProgress) / totalItems) * 100;
            setProgress(overallProgress);
          },
        });

        // Snap progress to completed item boundary
        setProgress(((i + 1) / totalItems) * 100);
      }

      setMediaItems([]);
      setTimeout(() => navigate(ROUTES.VIEW_PHOTOS), 1000);
    } catch (err) {
      console.error("Upload error:", err);
      setError(safeErrorMessage(err));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="gallery-upload-page">
      <div className="gallery-upload-container">
        <h1 className="gallery-upload-title">Upload existing photos &amp; videos</h1>
        <p className="gallery-upload-subtitle">
          Select up to {MAX_ITEMS} photos or short videos (up to {MAX_VIDEO_SECONDS} seconds each)
          from your laptop or desktop, add a short story for every item, and we&apos;ll do the rest.
        </p>

        <p className="gallery-upload-subtitle" style={{ marginTop: 8 }}>
          Limits: Photos up to <strong>{MAX_PHOTO_MB}MB</strong>, Videos up to{" "}
          <strong>{MAX_VIDEO_MB}MB</strong>.
        </p>

        {(schoolHashtag || cleanedDisplayName) && (
          <p className="gallery-upload-hint">
            We&apos;ll automatically add{" "}
            {schoolHashtag && <strong>{schoolHashtag}</strong>}
            {schoolHashtag && cleanedDisplayName && " and "}
            {cleanedDisplayName && <strong>#{cleanedDisplayName}</strong>} to each story.
          </p>
        )}

        <div className="file-picker-row">
          <label className="file-input-label">
            <span className="file-input-title">Choose files</span>
            <span className="file-input-subtitle">
              JPG images or short MP4/MOV videos – up to {MAX_ITEMS} at a time
            </span>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              ref={fileInputRef}
              onChange={handlePickMedia}
              disabled={mediaItems.length >= MAX_ITEMS || isUploading}
              className="file-input"
            />
          </label>
          <p className="photo-count">
            Selected: <strong>{mediaItems.length}</strong> / {MAX_ITEMS}
          </p>
        </div>

        {mediaItems.length > 0 && (
          <div className="photo-list">
            {mediaItems.map((item) => {
              const isVideo = item.mediaType === "VIDEO";
              const seconds =
                typeof item.durationMs === "number" && item.durationMs > 0
                  ? Math.round(item.durationMs / 1000)
                  : null;

              return (
                <div key={item.preview} className="photo-item">
                  <div className="photo-thumb-wrapper">
                    {isVideo ? (
                      <>
                        <video
                          src={item.preview}
                          className="photo-preview"
                          controls
                          muted
                          playsInline
                          onLoadedMetadata={(e) => handleVideoMetadata(item.preview, e)}
                        />
                        <button
                          onClick={() => handleDeleteItem(item.preview)}
                          className="delete-button"
                          disabled={isUploading}
                          aria-label="Remove this item"
                        >
                          ×
                        </button>
                        <span className="media-pill media-pill-video">
                          Video{seconds !== null && ` · ${seconds}s`}
                        </span>
                        {item.tooLong && (
                          <p className="video-warning">
                            This video is too long ({seconds}s). Max: {MAX_VIDEO_SECONDS}s.
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <img
                          src={item.preview}
                          alt={item.story || "Selected photo"}
                          className="photo-preview"
                        />
                        <button
                          onClick={() => handleDeleteItem(item.preview)}
                          className="delete-button"
                          disabled={isUploading}
                          aria-label="Remove this item"
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>

                  <textarea
                    value={item.story}
                    onChange={(e) => handleStoryChange(item.preview, e.target.value)}
                    placeholder="Add a short story or caption, plus any extra hashtags…"
                    className="story-input"
                    disabled={isUploading}
                  />
                </div>
              );
            })}
          </div>
        )}

        {mediaItems.length > 0 && (
          <button
            onClick={handleUpload}
            disabled={!canUpload || isUploading}
            className="upload-button"
          >
            {isUploading
              ? `Uploading… ${Math.round(progress)}%`
              : "Upload to your school"}
          </button>
        )}

        {isUploading && (
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div className="progress" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
};

export default GalleryUpload;
